import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ErrorView } from '@percent/admin-dashboard/common/components'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import { Button, Flex, Spacer, Table, TableBody, TablePagination, TableProvider, Typography } from '@percent/lemonade'
import { ProgramRow } from './ProgramRow'
import style from './Programs.module.scss'
import { ProgramsProps } from './Programs.types'

export function Programs({
  programs,
  isLoading,
  errorMessage,
  partnerId,
  nextPage,
  previousPage,
  totalPrograms,
  refreshPrograms
}: ProgramsProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const addNewProgramFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_ADD_NEW_PROGRAM)
  const internalFgaOrgvValidationSubmissionConfigurationsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_SUBMISSION_CONFIGURATIONS
  )
  const { userHasPermissions } = usePermissions()

  const canCreateProgram = internalFgaOrgvValidationSubmissionConfigurationsFeatureFlag
    ? userHasPermissions('validation_submission_configurations:can_create')
    : addNewProgramFeatureFlag

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const columns = [
    { id: t('typography.nameOfTheProgram') },
    { id: t('typography.status') },
    { id: t('typography.purpose') },
    { id: t('typography.lastUsedDate') },
    { id: '', props: { width: '15px' } }
  ]

  const goToAddNewProgram = () => {
    navigate(`/partners/${partnerId}/programs/add-new-program`)
  }

  return (
    <div className={style.tableContainer} style={{ width: '100%' }}>
      <TableProvider
        columns={columns}
        totalResults={totalPrograms}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t('typography.noResultsFound')}
        emptyTableDescription=""
      >
        <Flex className={style.toolbar} align="center" justify="space-between">
          <Typography variant="h3">{t('typography.programs')}</Typography>
          {canCreateProgram && (
            <Button variant="secondary" size="small" onClick={goToAddNewProgram}>
              {t('typography.addNewProgram')}
            </Button>
          )}
        </Flex>
        <Spacer size={4} axis="horizontal" />
        <Table>
          <TableBody>
            {programs?.map(row => (
              <ProgramRow key={row.id} row={row} refreshPrograms={refreshPrograms} />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalPrograms })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
    </div>
  )
}
